import React, { useMemo } from 'react'

import { DragOverlay, useDndContext } from '@dnd-kit/core'
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons'
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HStack, T } from 'govwell-ui'
import { ViewColumnTemplateFragment } from 'types/graphql'

import { useTheme } from 'src/hooks/use-theme'

// Renders the dragged item in a portal at its original size, decoupled from the sortable
// layout. This avoids a resizing issue where dragged items stretch to match taller/shorter
// sortable blocks (e.g. section headers with empty hints). Uses useDndContext() to read
// the active drag ID directly from dnd-kit, keeping re-renders isolated to this
// lightweight component rather than the full drawer.
type Props = {
  allTemplates: ViewColumnTemplateFragment[]
  displayLabelByColumnId: Map<string, string>
  shownColumnIds: Set<string>
}
const DrawerDragOverlay = ({ allTemplates, displayLabelByColumnId, shownColumnIds }: Props) => {
  const { active } = useDndContext()
  const { getTokenVal } = useTheme()
  const activeId = active?.id as string | undefined

  const activeTemplate = useMemo(() => {
    if (!activeId) {
      return undefined
    }
    return allTemplates.find((ct) => ct.generatedId === activeId)
  }, [activeId, allTemplates])

  const isVisible = activeId ? shownColumnIds.has(activeId) : false

  const hiddenIconStyle = useMemo<React.CSSProperties>(
    () => ({ color: getTokenVal('colorIcon') }),
    [getTokenVal]
  )

  if (!activeTemplate) {
    return <DragOverlay />
  }

  return (
    <DragOverlay>
      <HStack justify="space-between" gap={3} width="100%" className="bg-white shadow-md">
        <HStack gap={1} width="auto">
          <div className="p-2">
            <FontAwesomeIcon icon={faGripDotsVertical} />
          </div>
          <T.Body>
            {(activeId && displayLabelByColumnId.get(activeId)) ?? activeTemplate.labelSentenceCase}
          </T.Body>
        </HStack>
        <HStack wrap="nowrap" gap={1} width="auto" className="pr-1">
          <FontAwesomeIcon
            icon={isVisible ? faEye : faEyeSlash}
            style={isVisible ? undefined : hiddenIconStyle}
          />
        </HStack>
      </HStack>
    </DragOverlay>
  )
}

export default React.memo(DrawerDragOverlay)
